import CloningButtonsUtilsBase from '../cloningButtonsUtilsBase';
import { REGISTERED_APPS } from '../../shared/types/Apps';
import { logError, logInfo } from '../../shared/utils/logging';
import { debounce } from 'tiny-throttle';
import { getCheckoutButtonSelectors } from '../domUtils';

const CART_WATCHER_FOR_CLONE_BUTTONS_KEY = 'ajax_cart_watcher_selector';

export default class CandyCartCloningButtonUtils extends CloningButtonsUtilsBase {
  constructor(eventName: string) {
    super(eventName, REGISTERED_APPS.CHECKOUT_BUTTON);
    logInfo('loaded buttons', REGISTERED_APPS.CHECKOUT_BUTTON);
  }

  /*
   ** We decided to be very carefull and be sure that during refactoring
   ** checkout button with document listener, the original order of execution/creation
   ** in case of cloning the buttons won't change. This is the reason
   ** we didn't define this in constructor
   **
   ** This gets executed at most every 1000 ms
   */
  private debouncedCloneButtons: Function | undefined = this.cloneButtons
    ? debounce(this.cloneButtons.bind(this), 1000)
    : undefined;

  /*
   ** When CANDYCART_DISABLE_CLONING is set, we return an empty list of buttons which prevents
   ** their cloning. Its used mainly by support for problematic stores.
   */
  public getButtons(): HTMLInputElement[] {
    if (window.CANDYCART_DISABLE_CLONING) {
      return [];
    }

    return Array.from(
      document.querySelectorAll<HTMLInputElement>(getCheckoutButtonSelectors()),
    )
      .concat(
        Array.from(
          document.querySelectorAll<HTMLInputElement>(
            '[onclick*="checkout"]:not(.candy-cloned)',
          ),
        ).filter((element: HTMLInputElement) =>
          element.attributes
            .getNamedItem('onclick')
            ?.value.match(/location.*checkout/),
        ),
      )
      .filter(
        (element) =>
          !this.buttonPairs.find(
            (pair) =>
              pair.hiddenButton === element || pair.clonedButton === element,
          ),
      );
  }

  public buttonWatcher(): void {
    const callback = (mutationsList: any[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (
              node.querySelector &&
              node.querySelector(
                'form[action*="/cart"],form[action*="/checkout"],[type=submit]',
              )
            ) {
              this.cloneButtons(REGISTERED_APPS.CHECKOUT_BUTTON);
            }
          }
        }
      }
    };

    try {
      const observer = new MutationObserver(callback);
      const targetNode = document.body;
      observer.observe(targetNode, { childList: true, subtree: true });

      const forms = document.querySelectorAll<HTMLFormElement>(
        "form[action^='/checkout']",
      );
      for (let form of Array.from(forms)) {
        new MutationObserver(callback).observe(form, { attributes: true });
      }

      // Observe Candy Rack Slider Cart
      this.initCustomCartWatcherForCloneButtons('#candyrack-slider-cart');
      /*
       ** Hint: localStorage value CART_WATCHER_KEY is set later than
       ** initial loading of scripts from constructor.
       ** Observes the drawer cart.
       */
      const ajax_cart_watcher_selector =
        window.CANDYCART_AJAX_CART_WATCHER_SELECTOR ||
        localStorage.getItem(CART_WATCHER_FOR_CLONE_BUTTONS_KEY);
      this.initCustomCartWatcherForCloneButtons(ajax_cart_watcher_selector);
    } catch (error) {
      logError(
        'Failed to setup drawer cart observer',
        REGISTERED_APPS.CHECKOUT_BUTTON,
        error,
      );
    }
  }

  /*
   ** Triggers cloning of the button, not relevant for document listener
   */
  public initCustomCartWatcherForCloneButtons(selector: string | null): void {
    if (selector) {
      localStorage.setItem(CART_WATCHER_FOR_CLONE_BUTTONS_KEY, selector);
      const customCartWatcherTargetNode = document.querySelector(selector);
      if (customCartWatcherTargetNode) {
        logInfo(`Found ${CART_WATCHER_FOR_CLONE_BUTTONS_KEY}: ${selector}`);
        new MutationObserver(() => {
          this.debouncedCloneButtons?.();
        }).observe(customCartWatcherTargetNode, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }
  }
}
